import { usePortalQuery } from "@/state/hooks/usePortalQuery";
import { queryClient } from "@/state/queryClient";
import { queryKeys } from "@/state/queryKeys";
import trpc from "@/shared/utils/trpc";

export type MyEcosystemHeadLink = {
  id: string;
  status: string;
  verificationStatus: string;
  questionnaireSubmitted: boolean;
  hasUpcomingBooking?: boolean;
  ecosystemHead: {
    id: string;
    name: string;
    ecosystemIndustry: string | null;
    ecosystemCountry: string | null;
    ecosystemTargetCountry: string | null;
    corridorName?: string | null;
    badgeShow?: string | null;
    logoUrl?: string | null;
    expertiseTags?: string[];
    focusSectors?: Array<{ label: string; icon?: string }>;
    profileStatus?: "LIVE" | "COMING_SOON" | null;
    ecosystemHeadProfileStatus?: "LIVE" | "COMING_SOON" | null;
    joinRequestLimit?: number | null;
    joinRequestUsed?: number;
    joinRequestRemaining?: number | null;
    joinRequestFull?: boolean;
  };
};

export type MyEcosystemHeadLeftLink = {
  id: string;
  status: string;
  leftAt: string | null;
  chatUnlockedAt: string | null;
  ecosystemHead: MyEcosystemHeadLink["ecosystemHead"];
};

export type MyEcosystemHeadLinksData = {
  links: MyEcosystemHeadLink[];
  leftLinks: MyEcosystemHeadLeftLink[];
};

const EMPTY: MyEcosystemHeadLinksData = { links: [], leftLinks: [] };

/**
 * Single cached fetch for ecosystemHead.listMyEcosystemHeadLinks.
 * Shared by sidebar count, My Cluster, Explore corridors, and Account.
 * No refetchOnWindowFocus — focus/DevTools clicks must not re-hit the server.
 */
export function useMyEcosystemHeadLinksQuery(enabled = true) {
  return usePortalQuery({
    queryKey: queryKeys.badges.myEcosystemHeadLinks(),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<MyEcosystemHeadLinksData> => {
      try {
        const res = await trpc.ecosystemHead.listMyEcosystemHeadLinks.query();
        return {
          links: (res.links ?? []) as MyEcosystemHeadLink[],
          leftLinks: ((res as { leftLinks?: MyEcosystemHeadLeftLink[] }).leftLinks ??
            []) as MyEcosystemHeadLeftLink[],
        };
      } catch (err) {
        console.error("Error fetching my ecosystem head links:", err);
        return EMPTY;
      }
    },
  });
}

export function invalidateMyEcosystemHeadLinks() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.badges.myEcosystemHeadLinks() });
}
